import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { AxiosClient, AxiosUtils } from 'api/AxiosClient/AxiosClient';
import { config } from 'config';
import { Classified } from '@groupelacentrale/classified-core';
import { AwsPicture, ContactInfoProResponse, CreateClassifiedData, UpdateClassifiedRequest, VehicleType } from 'types';
import { REFERENCE_NOT_FOUND, SERVICE_DOWN } from 'api/errors';
import * as Sentry from '@sentry/react';

class ClassifiedManagement {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = AxiosClient.get({
      baseURL: config.URL_CLASSIFIED_MANAGEMENT,
    });
  }

  public async getClassified(reference: string): Promise<Classified> {
    return this.client
      .get<Classified>(`v2/classifieds/${reference}`, {
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(result => {
        return result.data;
      })
      .catch(e => {
        Sentry.captureException(e, {
          tags: {
            api: 'Classified Management',
            endpoint: 'get-classified',
            ...AxiosUtils.enrichTags(e),
          },
        });
        if (e.response || e.data) {
          const response: AxiosResponse<any> = e.response || e;
          return Promise.reject({ message: response.data.message });
        }
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }

  public async createClassified(data: CreateClassifiedData, depositType: VehicleType): Promise<Classified> {
    return this.client
      .post<Classified>(
        'v2/classifieds/deposit',
        {
          ...(data.registerNumber && data.registerNumber.length > 0 && { registerNumber: data.registerNumber }),
          ...(data.vin && data.vin.length > 0 && { vin: data.vin }),
          ngcData: data.ngcData,
          vehicleId: data.vehicle_id,
          ...(data.registerType &&
            data.registerType.length > 0 &&
            data.registerType !== 'UNKNOWN' && { registerType: data.registerType }),
          target: data.target,
        },
        {
          headers: AxiosClient.getAuthorizationHeader(),
        },
      )
      .then(result => {
        localStorage.setItem('depositType', depositType);
        localStorage.setItem('classifiedReference', result.data.reference);
        return result.data;
      })
      .catch(e => {
        Sentry.captureException(e, {
          tags: {
            api: 'Classified Management',
            endpoint: 'create-classified',
            ...AxiosUtils.enrichTags(e),
          },
        });
        if (e.response || e.data) {
          const response: AxiosResponse<any> = e.response || e;
          return Promise.reject({ message: response.data.error });
        }
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }

  public async updateClassified(data: UpdateClassifiedRequest): Promise<Classified> {
    const classifiedReference = localStorage.getItem('classifiedReference');
    if (!classifiedReference) {
      throw new Error(REFERENCE_NOT_FOUND);
    }
    data.reference = classifiedReference;
    return this.client
      .patch<Classified>('v2/classifieds/deposit', data, {
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(result => {
        localStorage.setItem('classifiedReference', result.data.reference);
        return result.data;
      })
      .catch(e => {
        Sentry.captureException(e, {
          tags: {
            api: 'Classified Management',
            endpoint: 'update-classified',
            ...AxiosUtils.enrichTags(e),
          },
        });
        if (e.response || e.data) {
          const response: AxiosResponse<any> = e.response || e;
          return Promise.reject({ message: response.data.error || response.data.message, code: response.data.code });
        }
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }

  public async publishClassified(publicationOptions?: string[]): Promise<Classified> {
    const classifiedReference = localStorage.getItem('classifiedReference');
    if (!classifiedReference) {
      throw new Error(REFERENCE_NOT_FOUND);
    }

    const body = publicationOptions
      ? {
          isSecuritel: publicationOptions.includes('SECURITEL'),
        }
      : undefined;

    return this.client
      .put<Classified>(`v2/classifieds/${classifiedReference}/publish`, body, {
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(result => {
        return result.data;
      })
      .catch((r: any) => {
        Sentry.captureException(r, {
          tags: {
            api: 'Classified Management',
            endpoint: 'publish-classified',
            ...AxiosUtils.enrichTags(r),
          },
        });
        if (r.response || r.data) {
          const response: AxiosResponse<any> = r.response || r.data;
          return Promise.reject({ message: response.data.error || response.data.message });
        }
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }

  public async uploadPhoto(classified: Classified, photos: AwsPicture[], media: string): Promise<void> {
    const data = {
      media,
      classifiedRef: classified.reference,
      category: classified.vehicle.category,
      photos: photos.map(p => ({
        action: p.action,
        type: p.type,
        bucketS3: p.bucket,
        keyS3: p.key,
        position: p.position,
      })),
    };

    return this.client
      .post<void>('/v2/classifieds/deposit/photos', data, {
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(result => {
        return result.data;
      })
      .catch(e => {
        Sentry.captureException(e, {
          tags: {
            api: 'Classified Management',
            endpoint: 'upload-photos',
            ...AxiosUtils.enrichTags(e),
          },
        });
        if (e.response) {
          const response: AxiosResponse<any> = e.response;
          return Promise.reject({ message: response.data.error || response.data.message });
        }
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }

  public async getProContactInfo(customerReference: string): Promise<ContactInfoProResponse> {
    try {
      const res = await this.client.get<ContactInfoProResponse>(`v2/customers/${customerReference}`, {
        headers: AxiosClient.getAuthorizationHeader(),
      });
      return res.data;
    } catch (e) {
      const response: AxiosResponse<any> = (e as AxiosError).response || (e as AxiosResponse);
      Sentry.captureException(e, {
        tags: {
          api: 'Classified Management',
          endpoint: 'customers',
          ...AxiosUtils.enrichTags(response),
        },
      });
      if (response) {
        return Promise.reject({ message: response.data.error });
      }
      return Promise.reject({ message: SERVICE_DOWN });
    }
  }

  public async renewClassified(isSecuritel: boolean): Promise<Classified> {
    const classifiedReference = localStorage.getItem('classifiedReference');
    if (!classifiedReference) {
      throw new Error(REFERENCE_NOT_FOUND);
    }

    return this.client
      .put<Classified>(
        `v2/classifieds/${classifiedReference}/renew`,
        { isSecuritel },
        {
          headers: AxiosClient.getAuthorizationHeader(),
        },
      )
      .then(result => {
        return result.data;
      })
      .catch((r: any) => {
        Sentry.captureException(r, {
          tags: {
            api: 'Classified Management',
            endpoint: 'renew-classified',
            ...AxiosUtils.enrichTags(r),
          },
        });
        if (r.response || r.data) {
          const response: AxiosResponse<any> = r.response || r;
          return Promise.reject({ message: response.data.error || response.data.message });
        }
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }
}
export const ClassifiedManagementService = new ClassifiedManagement();
